import Bowser from 'bowser';

interface PublicKeyCredentialCreationOptionsJSON {
    challenge: string;
    rp: { name: string; id: string };
    user: { id: string; name: string; displayName: string };
    pubKeyCredParams: Array<{ type: string; alg: number }>;
    timeout?: number;
    attestation?: string;
    authenticatorSelection?: {
        authenticatorAttachment?: string;
        residentKey?: string;
        userVerification?: string;
    };
    excludeCredentials?: Array<{
        type: string;
        id: string;
        transports?: string[];
    }>;
}

export function isWebAuthnSupported(): boolean {
    return typeof window !== 'undefined' && !!window.PublicKeyCredential;
}

export async function registerPasskey(
    optionsUrl: string,
    resultUrl: string,
    deviceName?: string
): Promise<void> {
    // Step 1: Get challenge from server.
    const optionsResponse = await fetch(optionsUrl, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceName }),
    });

    if (!optionsResponse.ok) {
        throw new Error('Failed to get registration options');
    }

    const options: PublicKeyCredentialCreationOptionsJSON = await optionsResponse.json();

    // Step 2: Convert base64url strings to ArrayBuffers.
    const publicKeyOptions: PublicKeyCredentialCreationOptions = {
        rp: options.rp,
        challenge: base64UrlToBuffer(options.challenge),
        user: {
            ...options.user,
            id: base64UrlToBuffer(options.user.id),
        },
        pubKeyCredParams: options.pubKeyCredParams as PublicKeyCredentialParameters[],
        timeout: options.timeout,
        attestation: options.attestation as AttestationConveyancePreference | undefined,
        authenticatorSelection: options.authenticatorSelection ? {
            authenticatorAttachment: options.authenticatorSelection.authenticatorAttachment as AuthenticatorAttachment | undefined,
            residentKey: options.authenticatorSelection.residentKey as ResidentKeyRequirement | undefined,
            userVerification: options.authenticatorSelection.userVerification as UserVerificationRequirement | undefined,
        } : undefined,
        excludeCredentials: options.excludeCredentials?.map((cred) => ({
            type: cred.type as PublicKeyCredentialType,
            id: base64UrlToBuffer(cred.id),
            transports: cred.transports as AuthenticatorTransport[] | undefined,
        })),
    };

    // Step 3: Call browser WebAuthn API.
    const credential = await navigator.credentials.create({
        publicKey: publicKeyOptions,
    });

    if (!credential || !(credential instanceof PublicKeyCredential)) {
        throw new Error('Failed to create credential');
    }

    // Step 4: Send credential to server.
    const response = credential.response as AuthenticatorAttestationResponse;

    const resultResponse = await fetch(resultUrl, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            id: credential.id,
            rawId: bufferToBase64Url(credential.rawId),
            response: {
                clientDataJSON: bufferToBase64Url(response.clientDataJSON),
                attestationObject: bufferToBase64Url(response.attestationObject),
                transports: response.getTransports?.() ?? [],
            },
            type: credential.type,
        }),
    });

    if (!resultResponse.ok) {
        throw new Error('Failed to register passkey');
    }
}

export async function loginWithPasskey(
    optionsUrl: string,
    resultUrl: string,
    username?: string
): Promise<{ user: { id: number; email: string; roles: string[] } }> {
    // Step 1: Get challenge from server.
    const optionsResponse = await fetch(optionsUrl, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username }),
    });

    if (!optionsResponse.ok) {
        throw new Error('Failed to get login options');
    }

    const options = await optionsResponse.json();

    // Step 2: Convert to browser format.
    const publicKeyOptions: PublicKeyCredentialRequestOptions = {
        ...options,
        challenge: base64UrlToBuffer(options.challenge),
        allowCredentials: options.allowCredentials?.map((cred: { type: string; id: string; transports?: string[] }) => ({
            type: cred.type as PublicKeyCredentialType,
            id: base64UrlToBuffer(cred.id),
            transports: cred.transports as AuthenticatorTransport[] | undefined,
        })),
    };

    // Step 3: Call browser WebAuthn API.
    const credential = await navigator.credentials.get({
        publicKey: publicKeyOptions,
    });

    if (!credential || !(credential instanceof PublicKeyCredential)) {
        throw new Error('Failed to get credential');
    }

    // Step 4: Send assertion to server.
    const response = credential.response as AuthenticatorAssertionResponse;

    const resultResponse = await fetch(resultUrl, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            id: credential.id,
            rawId: bufferToBase64Url(credential.rawId),
            response: {
                clientDataJSON: bufferToBase64Url(response.clientDataJSON),
                authenticatorData: bufferToBase64Url(response.authenticatorData),
                signature: bufferToBase64Url(response.signature),
                userHandle: response.userHandle
                    ? bufferToBase64Url(response.userHandle)
                    : null,
            },
            type: credential.type,
        }),
    });

    if (!resultResponse.ok) {
        throw new Error('Invalid passkey');
    }

    return resultResponse.json();
}

// Helper functions.
function base64UrlToBuffer(base64url: string): ArrayBuffer {
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const binary = atob(base64 + padding);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
}

function bufferToBase64Url(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (const byte of bytes) {
        binary += String.fromCharCode(byte);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

export function getDeviceName(): string {
    if (typeof window === 'undefined') {
        return 'Unknown Device';
    }

    const browser = Bowser.getParser(navigator.userAgent);
    const osName = browser.getOSName(true) || 'unknown';
    const browserName = browser.getBrowserName() || '';

    const platformMap: Record<string, string> = {
        'macos': 'Mac',
        'ios': 'iPhone',
        'android': 'Android',
        'windows': 'Windows',
        'linux': 'Linux',
    };

    const platform = platformMap[osName] || osName;

    if (browserName) {
        return `${platform} (${browserName})`;
    }
    return platform;
}
